// 模块注册表：侧边栏菜单、权限矩阵列、路由守卫的唯一数据源。
// 新增页面三步：① 本文件加一行 ② App.tsx 注册路由 ③ dashboard-layout.tsx MODULE_ICONS 加图标。
// 后台管理（/admin）不在此表：始终仅管理员可见，由 isAdmin 单独控制，不纳入可见性设置。

export type ModuleGroup = '经营' | '内容' | '系统';

// 模块徽标语义（全局徽标体系，见 docs/米粒AI_v1.0_开发文档.md 8.3 徽标设计规范）
export type ModuleBadge = 'new' | 'ren' | 'wip' | 'hot';

export interface ModuleDef {
  key: string;      // permissions.modules 中的键
  label: string;    // 侧边栏完整名称
  short: string;    // 权限表格表头短名
  group: ModuleGroup;
  path: string;     // 路由路径
  badge?: ModuleBadge; // 模块状态徽标：new=新上线 / ren=重大改动 / wip=开发中 / hot=热门
}

// 徽标样式映射（虚线边框 + 同色文字，深/浅主题通用；与热点页 BADGE_MAP 同源）
export const MODULE_BADGES: Record<ModuleBadge, { label: string; className: string }> = {
  new: { label: '新', className: 'border-dashed border-green-400 text-green-500' },
  ren: { label: '改', className: 'rounded border-dashed border-orange-400 text-orange-500' },
  wip: { label: '开发中', className: 'rounded border-dashed border-amber-400 text-amber-500' },
  hot: { label: '火', className: 'rounded border-dashed border-red-400 text-red-500' },
};

export const MODULE_GROUPS: ModuleGroup[] = ['经营', '内容', '系统'];

export const MODULES: ModuleDef[] = [
  { key: 'overview', label: '首页总览', short: '总览', group: '经营', path: '/' },
  { key: 'pulse', label: '创始人脉搏', short: '脉搏', group: '经营', path: '/pulse' },
  { key: 'growth', label: '经营增长看板', short: '增长', group: '经营', path: '/growth' },
  { key: 'project', label: '项目指挥台', short: '指挥台', group: '经营', path: '/project' },
  { key: 'persona', label: '我的账号', short: '账号', group: '内容', path: '/persona', badge: 'new' },
  { key: 'library', label: '内容库', short: '内容库', group: '内容', path: '/library' },
  { key: 'monitor', label: '对标监控', short: '对标', group: '内容', path: '/monitor', badge: 'hot' },
  { key: 'content', label: '内容工作台', short: '内容工作台', group: '内容', path: '/content', badge: 'wip' },
  { key: 'assets', label: '知识资产', short: '资产', group: '内容', path: '/assets', badge: 'wip' },
  { key: 'templates', label: '模板管理', short: '模板', group: '系统', path: '/templates' },
];

// 新用户默认可见性：经营模块默认关（商业数据不外露），内容/系统模块默认开（保持现状所有用户可见）。
// 注意：与后端 src/server/routers/auth.py 的 DEFAULT_PERMISSIONS 保持同步。
export const DEFAULT_MODULES: Record<string, boolean> = {
  overview: false, pulse: false, growth: false, project: false,
  diagnosis: true, monitor: true, library: true, persona: true, content: true, assets: true,
  templates: true,
};

export const DEFAULT_CHANNELS: Record<string, { max: number }> = {
  douyin: { max: 3 },
  xiaohongshu: { max: 3 },
  wechat: { max: 3 },
};

// 模块开启判定：未设置 = 开（兼容存量用户 permissions 中缺失的键）
export const isModuleEnabled = (modules: Record<string, boolean> | undefined, key: string): boolean =>
  (modules || {})[key] !== false;
